import type {
    ConfigDiagnostic,
    ConfigOwner,
    ConfigTarget,
    SettingsRegistryEntry,
    SettingsSnapshot,
} from "@magic-md/engine-client/config";
import type {
    SettingsPanelProps,
} from "@magic-md/extension-sdk";
import { useI18n, useT } from "@magic-md/i18n/react";
import { ArrowLeft, Blocks, Loader2 } from "lucide-react";
import { useDeferredValue, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { cn } from "../../../lib/utils";
import { getPlatformService } from "../../../platform";
import { pluginUIRegistry } from "../../../plugins/plugin-ui-registry";
import {
    extensionUIRegistry,
    makeExtensionTranslator,
    useExtensionHostStore,
    useExtensionSettingsPanel,
    useExtensionShellServices,
} from "../../../extensions";
import {
    buildExtensionSettingsModel,
    buildExtensionSettingsSnapshot,
    findAvailableTargetsForExtension,
    getEffectiveExtensionEnabled,
    isRecord,
    parseExtensionSettingsKey,
} from "../../../extensions/extension-settings-model";
import { useEditorStore } from "../../../store/editor-store";
import { useEngineOverviewStore } from "../../../store/engine-overview-store";
import { usePluginExecutionStore } from "../../../store/plugin-execution-store";
import { useSettingsStore } from "../../../store/settings-store";
import {
    buildLocalPluginCatalog,
    buildPluginCatalog,
    buildSettingGroups,
    filterSettingGroupsByExtensionSource,
    buildSidecarCatalog,
    buildVisibleSections,
    filterSettingGroupsBySource,
    filterSettingGroupsBySidecarSource,
    filterSettingGroups,
    matchesSearch,
    mergeCatalogMaps,
    sectionForOwner,
} from "./builders";
import { EFFECT_LABELS, SECTION_ICONS, SECTION_LABELS } from "./constants";
import { GroupDetailPanel, SettingDetailPanel } from "./DetailPanels";
import {
    Badge,
    DiagnosticsBanner,
    EmptyState,
    getDiagnosticRepairId,
    LayerHeader,
    MiniBadge,
} from "./LayoutComponents";
import { useSettingsTextResolver } from "./localization";
import { EntrySettingsPanel, GroupSettingsPanel } from "./PanelComponents";
import type { ExtensionSourceFilter } from "../../ExtensionList";
import type { PluginSourceFilter } from "../../../plugins/plugin-source-metadata";
import type { SidecarSourceFilter } from "../../SidecarList";
import {
    findAvailableTargetsForSection,
    findPreferredTargetForSection,
    resolveContextOwners,
    getCurrentLayerValue,
    isWritable,
    targetLabel,
} from "./resolvers";
import type { DetailMode, SettingGroup } from "./types";

export function SettingsCenter() {
    const t = useT("editor");
    const i18n = useI18n();
    const resolveText = useSettingsTextResolver();
    const close = useEditorStore((state) => state.setSidebarView);
    const enginePlugins = usePluginExecutionStore((state) => state.enginePlugins);
    const {
        searchQuery,
        activeSection,
        activeTarget,
        schema,
        snapshot,
        drafts,
        staleKeys,
        loadingSchema,
        loadingSnapshot,
        savingKeys,
        error,
        ensureLoaded,
        loadSchema,
        loadSnapshot,
        commitValue,
        clearValue,
        setDraft,
        discardDraft,
        setSearchQuery,
        setActiveSection,
        setActiveTarget,
        clearStale,
    } = useSettingsStore();
    const deferredSearch = useDeferredValue(searchQuery.trim().toLowerCase());
    const [manualReloading, setManualReloading] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [detailMode, setDetailMode] = useState<DetailMode>("form");
    const [pluginSourceFilter, setPluginSourceFilter] =
        useState<PluginSourceFilter>("all");
    const [sidecarSourceFilter, setSidecarSourceFilter] =
        useState<SidecarSourceFilter>("all");
    const [extensionSourceFilter, setExtensionSourceFilter] =
        useState<ExtensionSourceFilter>("all");
    const [pluginRegistryVersion, setPluginRegistryVersion] = useState(0);
    const [repairingDiagnosticIds, setRepairingDiagnosticIds] = useState<string[]>([]);
    const [showExtensions, setShowExtensions] = useState(false);
    const [extensionDrafts, setExtensionDrafts] = useState<Record<string, unknown>>({});
    const [extensionSavingKeys, setExtensionSavingKeys] = useState<string[]>([]);
    const [extensionServiceSnapshot, setExtensionServiceSnapshot] =
        useState<SettingsSnapshot | null>(null);
    const [loadingExtensionSnapshot, setLoadingExtensionSnapshot] = useState(false);
    const extensionSettings = useExtensionHostStore((state) => state.settings);
    const extensionDefaults = useExtensionHostStore((state) => state.defaults);
    const extensionEnabled = useExtensionHostStore((state) => state.enabled);
    const extensionRegistryRevision = useSyncExternalStore(
        (onChange) => extensionUIRegistry.subscribe(onChange),
        () => extensionUIRegistry.revision(),
        () => extensionUIRegistry.revision(),
    );
    const overviewPlugins = useEngineOverviewStore((s) => s.plugins);
    const overviewSidecars = useEngineOverviewStore((s) => s.sidecars);
    const retainOverviewScopes = useEngineOverviewStore((s) => s.retainScopes);
    const refreshOverviewNow = useEngineOverviewStore((s) => s.refreshNow);

    useEffect(() => {
        void ensureLoaded();
    }, [ensureLoaded]);

    useEffect(
        () =>
            pluginUIRegistry.subscribe(() => {
                setPluginRegistryVersion((value) => value + 1);
            }),
        [],
    );

    useEffect(() => {
        return retainOverviewScopes(["plugins", "sidecars"]);
    }, [retainOverviewScopes]);

    const capabilities =
        snapshot?.capabilities ?? getPlatformService().settings.capabilities();
    const availableTargets: ConfigTarget[] =
        capabilities.availableTargets.length > 0
            ? capabilities.availableTargets
            : ["user"];
    const primarySchema = useMemo(
        () => schema.filter((entry) => entry.owner !== "extension"),
        [schema],
    );
    const localPluginCatalog = useMemo(
        () => buildLocalPluginCatalog(),
        [i18n.fallbackLocale, i18n.locale, pluginRegistryVersion],
    );
    const enginePluginCatalog = useMemo(
        () => buildPluginCatalog(enginePlugins),
        [enginePlugins],
    );
    const pluginCatalog = useMemo(
        () =>
            mergeCatalogMaps(
                enginePluginCatalog,
                buildPluginCatalog(overviewPlugins),
                localPluginCatalog,
            ),
        [enginePluginCatalog, localPluginCatalog, overviewPlugins],
    );
    const sidecarCatalog = useMemo(
        () => buildSidecarCatalog(
            overviewSidecars,
            i18n.locale,
            i18n.fallbackLocale,
        ),
        [i18n.fallbackLocale, i18n.locale, overviewSidecars],
    );
    const extensionSettingsModel = useMemo(
        () => {
            void extensionRegistryRevision;
            return buildExtensionSettingsModel();
        },
        [extensionRegistryRevision, i18n.fallbackLocale, i18n.locale],
    );

    const syncExtensionEffectiveEnabled = (
        serviceSnapshot: SettingsSnapshot | null,
    ) => {
        const store = useExtensionHostStore.getState();
        if (extensionSettingsModel.entries.length === 0) {
            store.setEffectiveEnabled({});
            return;
        }
        const mergedSnapshot = buildExtensionSettingsSnapshot({
            entries: extensionSettingsModel.entries,
            serviceSnapshot,
            settings: store.settings,
            defaults: store.defaults,
            enabled: store.enabled,
        });
        store.setEffectiveEnabled(
            getEffectiveExtensionEnabled(
                mergedSnapshot,
                extensionSettingsModel.entries,
            ),
        );
    };

    const loadExtensionSnapshot = async (): Promise<SettingsSnapshot | null> => {
        if (extensionSettingsModel.entries.length === 0) {
            setExtensionServiceSnapshot(null);
            syncExtensionEffectiveEnabled(null);
            return null;
        }
        setLoadingExtensionSnapshot(true);
        try {
            const next = await getPlatformService().settings.getSnapshot(
                { owner: "extension" },
                extensionSettingsModel.entries,
            );
            setExtensionServiceSnapshot(next);
            syncExtensionEffectiveEnabled(next);
            return next;
        } catch {
            setExtensionServiceSnapshot(null);
            syncExtensionEffectiveEnabled(null);
            return null;
        } finally {
            setLoadingExtensionSnapshot(false);
        }
    };

    useEffect(() => {
        void loadExtensionSnapshot();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extensionSettingsModel.entries]);

    const extensionSnapshot = useMemo(
        () =>
            buildExtensionSettingsSnapshot({
                entries: extensionSettingsModel.entries,
                serviceSnapshot: extensionServiceSnapshot,
                settings: extensionSettings,
                defaults: extensionDefaults,
                enabled: extensionEnabled,
            }),
        [
            extensionDefaults,
            extensionEnabled,
            extensionServiceSnapshot,
            extensionSettings,
            extensionSettingsModel.entries,
        ],
    );

    useEffect(() => {
        if (!availableTargets.includes(activeTarget)) {
            setActiveTarget(availableTargets[0] ?? "user");
        }
    }, [activeTarget, availableTargets, setActiveTarget]);

    const visibleSections = useMemo(
        () => buildVisibleSections(primarySchema, capabilities, activeTarget),
        [activeTarget, capabilities, primarySchema],
    );
    const allSections = useMemo(
        () => buildVisibleSections(primarySchema, capabilities),
        [capabilities, primarySchema],
    );
    const diagnostics = snapshot?.diagnostics ?? [];
    const sectionTargets = useMemo(
        () =>
            Object.fromEntries(
                allSections.map((section) => [
                    section,
                    findAvailableTargetsForSection(section, primarySchema, capabilities),
                ]),
            ) as Partial<Record<(typeof allSections)[number], ConfigTarget[]>>,
        [allSections, capabilities, primarySchema],
    );
    const sectionPreferredTargets = useMemo(
        () =>
            Object.fromEntries(
                allSections.map((section) => [
                    section,
                    sectionTargets[section]?.[0] ?? null,
                ]),
            ) as Partial<Record<(typeof allSections)[number], ConfigTarget | null>>,
        [allSections, sectionTargets],
    );

    useEffect(() => {
        if (!visibleSections.includes(activeSection)) {
            const preferredTarget = findPreferredTargetForSection(
                activeSection,
                primarySchema,
                capabilities,
            );
            if (preferredTarget && preferredTarget !== activeTarget) {
                setActiveTarget(preferredTarget);
                return;
            }
            if (allSections.includes(activeSection)) {
                return;
            }
            setActiveSection(visibleSections[0] ?? "editor");
        }
    }, [
        activeSection,
        activeTarget,
        allSections,
        capabilities,
        primarySchema,
        setActiveSection,
        setActiveTarget,
        visibleSections,
    ]);

    const sectionEntries = useMemo(() => {
        return primarySchema
            .filter((entry) => visibleSections.includes(sectionForOwner(entry.owner)))
            .filter((entry) => entry.targets.includes(activeTarget))
            .filter((entry) => sectionForOwner(entry.owner) === activeSection)
            .sort((left, right) => left.key.localeCompare(right.key));
    }, [activeSection, activeTarget, primarySchema, visibleSections]);
    const filteredEntries = useMemo(() => {
        return sectionEntries
            .filter((entry) => matchesSearch(entry, deferredSearch, resolveText))
            .sort((left, right) => left.key.localeCompare(right.key));
    }, [deferredSearch, resolveText, sectionEntries]);
    const pluginGroups = useMemo(
        () => buildSettingGroups(sectionEntries, "plugin", pluginCatalog),
        [pluginCatalog, sectionEntries],
    );
    const searchedPluginGroups = useMemo(
        () => filterSettingGroups(pluginGroups, deferredSearch, resolveText),
        [deferredSearch, pluginGroups, resolveText],
    );
    const filteredPluginGroups = useMemo(
        () => filterSettingGroupsBySource(searchedPluginGroups, pluginSourceFilter),
        [pluginSourceFilter, searchedPluginGroups],
    );
    const sidecarGroups = useMemo(
        () => buildSettingGroups(sectionEntries, "sidecar", sidecarCatalog),
        [sectionEntries, sidecarCatalog],
    );
    const searchedSidecarGroups = useMemo(
        () => filterSettingGroups(sidecarGroups, deferredSearch, resolveText),
        [deferredSearch, sidecarGroups, resolveText],
    );
    const filteredSidecarGroups = useMemo(
        () => filterSettingGroupsBySidecarSource(searchedSidecarGroups, sidecarSourceFilter),
        [searchedSidecarGroups, sidecarSourceFilter],
    );
    const extensionGroups = useMemo(
        () =>
            buildSettingGroups(
                extensionSettingsModel.entries,
                "extension",
                extensionSettingsModel.catalog,
            ),
        [extensionSettingsModel.catalog, extensionSettingsModel.entries],
    );
    const searchedExtensionGroups = useMemo(
        () => filterSettingGroups(extensionGroups, deferredSearch, resolveText),
        [deferredSearch, extensionGroups, resolveText],
    );
    const filteredExtensionGroups = useMemo(
        () => filterSettingGroupsByExtensionSource(
            searchedExtensionGroups,
            extensionSourceFilter,
        ),
        [extensionSourceFilter, searchedExtensionGroups],
    );
    const extensionItemIds = useMemo(
        () => filteredExtensionGroups.map((group) => group.id),
        [filteredExtensionGroups],
    );
    const extensionTargets = useMemo(
        () =>
            findAvailableTargetsForExtension(
                extensionSettingsModel.entries,
                extensionSnapshot.capabilities,
            ),
        [extensionSettingsModel.entries, extensionSnapshot.capabilities],
    );

    useEffect(() => {
        if (!showExtensions) return;
        if (extensionTargets.length === 0) return;
        if (!extensionTargets.includes(activeTarget)) {
            setActiveTarget(extensionTargets[0] ?? "user");
        }
    }, [activeTarget, extensionTargets, setActiveTarget, showExtensions]);

    const sectionItems = useMemo(() => {
        switch (activeSection) {
            case "plugin":
                return filteredPluginGroups.map((group) => group.id);
            case "sidecar":
                return filteredSidecarGroups.map((group) => group.id);
            default:
                return filteredEntries.map((entry) => entry.key);
        }
    }, [
        activeSection,
        filteredEntries,
        filteredPluginGroups,
        filteredSidecarGroups,
    ]);

    const handleManualReload = async () => {
        if (showExtensions) {
            setExtensionDrafts({});
            await loadExtensionSnapshot();
            return;
        }
        setManualReloading(true);
        try {
            await getPlatformService().settings.reload(
                activeTarget === "user" ? { target: "user" } : { target: activeTarget },
            );
            await Promise.all([
                loadSchema(),
                loadSnapshot(),
                refreshOverviewNow(["plugins", "sidecars"]),
            ]);
            clearStale();
        } catch (err) {
            useSettingsStore.setState({
                error: err instanceof Error ? err.message : String(err),
            });
        } finally {
            setManualReloading(false);
        }
    };

    const handleRepairDiagnostic = async (diagnostic: ConfigDiagnostic) => {
        const repairId = getDiagnosticRepairId(diagnostic);
        if (
            !repairId ||
            !diagnostic.owner ||
            !diagnostic.key ||
            (diagnostic.target !== "user" &&
                diagnostic.target !== "workspace" &&
                diagnostic.target !== "remoteHost")
        ) {
            return;
        }

        setRepairingDiagnosticIds((items) => [...new Set([...items, repairId])]);
        try {
            await clearValue(diagnostic.owner, diagnostic.target, [diagnostic.key]);
        } finally {
            setRepairingDiagnosticIds((items) =>
                items.filter((item) => item !== repairId),
            );
        }
    };

    useEffect(() => {
        if (showExtensions) {
            if (!extensionItemIds.includes(selectedItemId ?? "")) {
                setSelectedItemId(extensionItemIds[0] ?? null);
            }
            return;
        }
        if (!sectionItems.includes(selectedItemId ?? "")) {
            setSelectedItemId(sectionItems[0] ?? null);
        }
    }, [extensionItemIds, sectionItems, selectedItemId, showExtensions]);

    useEffect(() => {
        setDetailMode("form");
    }, [activeSection, activeTarget, selectedItemId, showExtensions]);

    const selectedEntry =
        activeSection === "editor" || activeSection === "runtime"
            ? (filteredEntries.find((entry) => entry.key === selectedItemId) ?? null)
            : null;
    const selectedPluginGroup =
        activeSection === "plugin"
            ? (filteredPluginGroups.find((group) => group.id === selectedItemId) ??
                null)
            : null;
    const selectedSidecarGroup =
        activeSection === "sidecar"
            ? (filteredSidecarGroups.find((group) => group.id === selectedItemId) ??
                null)
            : null;
    const selectedExtensionGroup = showExtensions
        ? (filteredExtensionGroups.find((group) => group.id === selectedItemId) ??
            null)
        : null;
    const activeOwners = useMemo(
        () =>
            resolveContextOwners({
                activeSection,
                sectionEntries,
                selectedEntry,
                selectedPluginGroup,
                selectedSidecarGroup,
            }),
        [
            activeSection,
            sectionEntries,
            selectedEntry,
            selectedPluginGroup,
            selectedSidecarGroup,
        ],
    );
    const headerTarget: ConfigTarget = activeTarget;
    const headerSection = showExtensions ? "editor" : activeSection;
    const headerOwners: ConfigOwner[] = showExtensions ? ["extension"] : activeOwners;
    const headerSnapshot = showExtensions ? extensionSnapshot : snapshot;

    const handleExtensionDraftChange = (key: string, value: unknown) => {
        setExtensionDrafts((state) => ({ ...state, [key]: value }));
    };
    const handleExtensionDiscardDraft = (key: string) => {
        setExtensionDrafts((state) => {
            if (!Object.hasOwn(state, key)) return state;
            const next = { ...state };
            delete next[key];
            return next;
        });
    };
    const withExtensionSaving = async (
        key: string,
        run: () => Promise<void> | void,
    ) => {
        setExtensionSavingKeys((keys) => [...new Set([...keys, key])]);
        try {
            await run();
        } finally {
            setExtensionSavingKeys((keys) => keys.filter((item) => item !== key));
        }
    };
    const handleExtensionCommit = async (
        entry: SettingsRegistryEntry,
        value: unknown,
    ) => {
        const parsed = parseExtensionSettingsKey(entry.key);
        if (!parsed) return;
        await withExtensionSaving(entry.key, async () => {
            const store = useExtensionHostStore.getState();
            if (activeTarget === "user") {
                if (parsed.kind === "enabled") {
                    store.setEnabled(parsed.extensionId, value !== false);
                } else {
                    const nextSettings = isRecord(value) ? value : {};
                    store.setSettings(parsed.extensionId, nextSettings);
                }
                syncExtensionEffectiveEnabled(extensionServiceSnapshot);
            } else {
                await getPlatformService().settings.setValue(
                    {
                        owner: "extension",
                        target: activeTarget,
                        dotPath: entry.key,
                        value,
                    },
                    extensionSettingsModel.entries,
                );
                await loadExtensionSnapshot();
            }
            if (parsed.kind === "settings") {
                await getPlatformService().engine.extensionSettingsUpdate?.({
                    extensionId: parsed.extensionId,
                    settings:
                        activeTarget === "user"
                            ? store.getSettings(parsed.extensionId)
                            : {},
                });
            } else if (value === false) {
                await getPlatformService().engine.extensionDisposeRuntime?.({
                    extensionId: parsed.extensionId,
                });
            }
            handleExtensionDiscardDraft(entry.key);
        });
    };
    const handleExtensionClear = async (entry: SettingsRegistryEntry) => {
        const parsed = parseExtensionSettingsKey(entry.key);
        if (!parsed) return;
        await withExtensionSaving(entry.key, async () => {
            const store = useExtensionHostStore.getState();
            if (activeTarget === "user") {
                if (parsed.kind === "enabled") {
                    store.setEnabled(parsed.extensionId, true);
                } else {
                    store.clearSettings(parsed.extensionId);
                }
                syncExtensionEffectiveEnabled(extensionServiceSnapshot);
            } else {
                await getPlatformService().settings.setValue(
                    {
                        owner: "extension",
                        target: activeTarget,
                        unset: [entry.key],
                    },
                    extensionSettingsModel.entries,
                );
                await loadExtensionSnapshot();
            }
            if (parsed.kind === "settings") {
                await getPlatformService().engine.extensionSettingsUpdate?.({
                    extensionId: parsed.extensionId,
                    settings:
                        activeTarget === "user"
                            ? store.getSettings(parsed.extensionId)
                            : {},
                });
            }
            handleExtensionDiscardDraft(entry.key);
        });
    };
    const handleExtensionReload = async () => {
        setExtensionDrafts({});
        await loadExtensionSnapshot();
    };

    return (
        <div className="flex h-full min-h-0 bg-background">
            <aside className="flex w-[320px] shrink-0 flex-col border-r border-border bg-muted/20">
                <div className="flex h-8 items-center justify-between border-b border-border px-3 shrink-0">
                    <span className="truncate text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t("settings.title")}
                    </span>
                    <button
                        type="button"
                        className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                        onClick={() => close("settings")}
                        title={t("settings.close")}
                    >
                        <ArrowLeft size={14} />
                    </button>
                </div>

                <nav className="space-y-2 p-2">
                    {allSections.map((section) => {
                        const targets = sectionTargets[section] ?? [];
                        const sectionPreferredTarget =
                            sectionPreferredTargets[section] ?? null;
                        const visibleInCurrentLayer = visibleSections.includes(section);
                        const rowActive = !showExtensions && activeSection === section;

                        return (
                            <div
                                key={section}
                                className={cn(
                                    "overflow-hidden rounded-lg border transition-colors",
                                    rowActive
                                        ? "border-primary/20 bg-primary/5"
                                        : "border-border/30",
                                )}
                            >
                                <div
                                    data-settings-section-row={section}
                                    className={cn(
                                        "flex h-8 items-center gap-1.5 px-2.5 transition-colors",
                                        rowActive ? "bg-primary/5" : "hover:bg-muted/30",
                                    )}
                                >
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (
                                                !visibleInCurrentLayer &&
                                                sectionPreferredTarget &&
                                                sectionPreferredTarget !== activeTarget
                                            ) {
                                                setActiveTarget(sectionPreferredTarget);
                                            }
                                            setShowExtensions(false);
                                            setActiveSection(section);
                                        }}
                                        className={cn(
                                            "flex min-w-0 flex-1 items-center gap-1.5 text-left transition-colors",
                                            rowActive
                                                ? "text-foreground"
                                                : visibleInCurrentLayer
                                                    ? "text-muted-foreground hover:text-foreground"
                                                    : "text-muted-foreground/80 hover:text-foreground",
                                        )}
                                    >
                                        <span className="shrink-0 opacity-80 [&>svg]:h-3 [&>svg]:w-3">
                                            {SECTION_ICONS[section]}
                                        </span>
                                        <span className="min-w-0 flex-1 truncate text-[10px] font-semibold uppercase tracking-wider">
                                            {resolveText(SECTION_LABELS[section])}
                                        </span>
                                    </button>
                                    {targets.length > 1 ? (
                                        <div className="inline-flex shrink-0 rounded-md border border-border/80 bg-background/80 p-0.5">
                                            {targets.map((target) => {
                                                const targetActive =
                                                    rowActive && activeTarget === target;
                                                return (
                                                    <button
                                                        key={target}
                                                        type="button"
                                                        onClick={() => {
                                                            setShowExtensions(false);
                                                            setActiveSection(section);
                                                            setActiveTarget(target);
                                                        }}
                                                        className={cn(
                                                            "rounded px-1.5 py-0.5 text-[10px] leading-none transition-colors",
                                                            targetActive
                                                                ? "bg-primary/10 text-primary"
                                                                : "text-muted-foreground hover:text-foreground",
                                                        )}
                                                    >
                                                        {targetLabel(target, resolveText)}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}

                    {/* Extensions — third extension class settings */}
                    <div
                        className={cn(
                            "overflow-hidden rounded-lg border transition-colors",
                            showExtensions
                                ? "border-primary/20 bg-primary/5"
                                : "border-border/30",
                        )}
                    >
                        <div
                            data-settings-section-row="extension"
                            className={cn(
                                "flex h-8 items-center gap-1.5 px-2.5 transition-colors",
                                showExtensions ? "bg-primary/5" : "hover:bg-muted/30",
                            )}
                        >
                            <button
                                type="button"
                                onClick={() => {
                                    setShowExtensions(true);
                                    if (
                                        extensionTargets.length > 0 &&
                                        !extensionTargets.includes(activeTarget)
                                    ) {
                                        setActiveTarget(extensionTargets[0]);
                                    }
                                }}
                                className={cn(
                                    "flex min-w-0 flex-1 items-center gap-1.5 text-left transition-colors",
                                    showExtensions
                                        ? "text-foreground"
                                        : "text-muted-foreground hover:text-foreground",
                                )}
                            >
                                <span className="shrink-0 opacity-80 [&>svg]:h-3 [&>svg]:w-3">
                                    <Blocks size={14} />
                                </span>
                                <span className="min-w-0 flex-1 truncate text-[10px] font-semibold uppercase tracking-wider">
                                    {t("engine.sections.extensions")}
                                </span>
                            </button>
                            {extensionTargets.length > 1 ? (
                                <div className="inline-flex shrink-0 rounded-md border border-border/80 bg-background/80 p-0.5">
                                    {extensionTargets.map((target) => {
                                        const targetActive =
                                            showExtensions && activeTarget === target;
                                        return (
                                            <button
                                                key={target}
                                                type="button"
                                                onClick={() => {
                                                    setShowExtensions(true);
                                                    setActiveTarget(target);
                                                }}
                                                className={cn(
                                                    "rounded px-1.5 py-0.5 text-[10px] leading-none transition-colors",
                                                    targetActive
                                                        ? "bg-primary/10 text-primary"
                                                        : "text-muted-foreground hover:text-foreground",
                                                )}
                                            >
                                                {targetLabel(target, resolveText)}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </nav>
            </aside>

            <main className="flex min-w-0 flex-1 flex-col">
                <LayerHeader
                    target={headerTarget}
                    section={headerSection}
                    sectionIcon={showExtensions ? <Blocks size={14} /> : undefined}
                    sectionLabel={
                        showExtensions ? t("engine.sections.extensions") : undefined
                    }
                    owners={headerOwners}
                    snapshot={headerSnapshot}
                    loading={
                        showExtensions
                            ? loadingExtensionSnapshot
                            : loadingSchema || loadingSnapshot
                    }
                    reloading={manualReloading}
                    onReload={handleManualReload}
                />

                {!showExtensions && error ? (
                    <div className="border-b border-destructive/20 bg-destructive/5 px-4 py-2 text-sm text-destructive">
                        {error}
                    </div>
                ) : null}

                {!showExtensions && diagnostics.length > 0 ? (
                    <div className="border-b border-border px-3 py-3">
                        <DiagnosticsBanner
                            diagnostics={diagnostics}
                            repairingIds={repairingDiagnosticIds}
                            onRepairDiagnostic={handleRepairDiagnostic}
                        />
                    </div>
                ) : null}

                <div className="flex min-h-0 flex-1">
                    {showExtensions ? (
                        <GroupSettingsPanel
                            title={t("settings.list.extensions")}
                            groups={searchedExtensionGroups}
                            selectedId={selectedExtensionGroup?.id ?? null}
                            onSelect={setSelectedItemId}
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery}
                            variant="extension"
                            extensionSourceFilter={extensionSourceFilter}
                            onExtensionSourceFilterChange={setExtensionSourceFilter}
                            target={activeTarget}
                            snapshot={extensionSnapshot}
                            drafts={extensionDrafts}
                            savingKeys={extensionSavingKeys}
                            onToggleEnabled={async (entry, value) => {
                                await handleExtensionCommit(entry, value);
                            }}
                            detail={
                                selectedExtensionGroup ? (
                                    <ExtensionSettingsDetailPanel
                                        group={selectedExtensionGroup}
                                        target={activeTarget}
                                        snapshot={extensionSnapshot}
                                        drafts={extensionDrafts}
                                        staleKeys={[]}
                                        savingKeys={extensionSavingKeys}
                                        detailMode={detailMode}
                                        onDetailModeChange={setDetailMode}
                                        onDraftChange={handleExtensionDraftChange}
                                        onDiscardDraft={handleExtensionDiscardDraft}
                                        onCommit={handleExtensionCommit}
                                        onClear={handleExtensionClear}
                                        onReload={handleExtensionReload}
                                    />
                                ) : null
                            }
                        />
                    ) : !snapshot && loadingSnapshot ? (
                        <EmptyState message={t("settings.empty.loadingSnapshot")} loading />
                    ) : activeSection === "plugin" ? (
                        <GroupSettingsPanel
                            title={t("settings.list.plugins")}
                            groups={searchedPluginGroups}
                            selectedId={selectedPluginGroup?.id ?? null}
                            onSelect={setSelectedItemId}
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery}
                            variant="plugin"
                            sourceFilter={pluginSourceFilter}
                            onSourceFilterChange={setPluginSourceFilter}
                            target={activeTarget}
                            snapshot={snapshot}
                            drafts={drafts}
                            savingKeys={savingKeys}
                            onToggleEnabled={async (entry, value) => {
                                await commitValue({
                                    owner: entry.owner,
                                    target: activeTarget,
                                    dotPath: entry.key,
                                    value,
                                });
                            }}
                            detail={
                                selectedPluginGroup ? (
                                    <GroupDetailPanel
                                        group={selectedPluginGroup}
                                        target={activeTarget}
                                        snapshot={snapshot}
                                        drafts={drafts}
                                        staleKeys={staleKeys}
                                        savingKeys={savingKeys}
                                        detailMode={detailMode}
                                        onDetailModeChange={setDetailMode}
                                        onDraftChange={(key, value) => setDraft(key, value)}
                                        onDiscardDraft={(key) => discardDraft(key)}
                                        onCommit={async (entry, value) => {
                                            await commitValue({
                                                owner: entry.owner,
                                                target: activeTarget,
                                                dotPath: entry.key,
                                                value,
                                            });
                                        }}
                                        onClear={async (entry) => {
                                            await clearValue(entry.owner, activeTarget, [entry.key]);
                                        }}
                                        onReload={() => loadSnapshot()}
                                    />
                                ) : null
                            }
                        />
                    ) : activeSection === "sidecar" ? (
                        <GroupSettingsPanel
                            title={t("settings.list.sidecars")}
                            groups={searchedSidecarGroups}
                            selectedId={selectedSidecarGroup?.id ?? null}
                            onSelect={setSelectedItemId}
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery}
                            variant="sidecar"
                            sidecarSourceFilter={sidecarSourceFilter}
                            onSidecarSourceFilterChange={setSidecarSourceFilter}
                            target={activeTarget}
                            snapshot={snapshot}
                            drafts={drafts}
                            savingKeys={savingKeys}
                            onToggleEnabled={async (entry, value) => {
                                await commitValue({
                                    owner: entry.owner,
                                    target: activeTarget,
                                    dotPath: entry.key,
                                    value,
                                });
                            }}
                            detail={
                                selectedSidecarGroup ? (
                                    <GroupDetailPanel
                                        group={selectedSidecarGroup}
                                        target={activeTarget}
                                        snapshot={snapshot}
                                        drafts={drafts}
                                        staleKeys={staleKeys}
                                        savingKeys={savingKeys}
                                        detailMode={detailMode}
                                        onDetailModeChange={setDetailMode}
                                        onDraftChange={(key, value) => setDraft(key, value)}
                                        onDiscardDraft={(key) => discardDraft(key)}
                                        onCommit={async (entry, value) => {
                                            await commitValue({
                                                owner: entry.owner,
                                                target: activeTarget,
                                                dotPath: entry.key,
                                                value,
                                            });
                                        }}
                                        onClear={async (entry) => {
                                            await clearValue(entry.owner, activeTarget, [entry.key]);
                                        }}
                                        onReload={() => loadSnapshot()}
                                    />
                                ) : null
                            }
                        />
                    ) : (
                        <EntrySettingsPanel
                            title={resolveText(SECTION_LABELS[activeSection])}
                            section={activeSection}
                            entries={filteredEntries}
                            selectedKey={selectedEntry?.key ?? null}
                            onSelect={setSelectedItemId}
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery}
                            target={activeTarget}
                            snapshot={snapshot}
                            drafts={drafts}
                            detail={
                                selectedEntry ? (
                                    <SettingDetailPanel
                                        entry={selectedEntry}
                                        target={activeTarget}
                                        snapshot={snapshot}
                                        draftValue={drafts[selectedEntry.key]}
                                        stale={staleKeys.includes(selectedEntry.key)}
                                        saving={savingKeys.includes(selectedEntry.key)}
                                        detailMode={detailMode}
                                        onDetailModeChange={setDetailMode}
                                        onDraftChange={(value) =>
                                            setDraft(selectedEntry.key, value)
                                        }
                                        onDiscardDraft={() => discardDraft(selectedEntry.key)}
                                        onCommit={async (value) => {
                                            await commitValue({
                                                owner: selectedEntry.owner,
                                                target: activeTarget,
                                                dotPath: selectedEntry.key,
                                                value,
                                            });
                                        }}
                                        onClear={async () => {
                                            await clearValue(selectedEntry.owner, activeTarget, [
                                                selectedEntry.key,
                                            ]);
                                        }}
                                        onReload={() => loadSnapshot()}
                                    />
                                ) : null
                            }
                        />
                    )}
                </div>
            </main>
        </div>
    );
}

function ExtensionSettingsDetailPanel({
    group,
    target,
    snapshot,
    drafts,
    staleKeys,
    savingKeys,
    detailMode,
    onDetailModeChange,
    onDraftChange,
    onDiscardDraft,
    onCommit,
    onClear,
    onReload,
}: {
    group: SettingGroup;
    target: ConfigTarget;
    snapshot: SettingsSnapshot | null;
    drafts: Record<string, unknown>;
    staleKeys: string[];
    savingKeys: string[];
    detailMode: DetailMode;
    onDetailModeChange: (mode: DetailMode) => void;
    onDraftChange: (key: string, value: unknown) => void;
    onDiscardDraft: (key: string) => void;
    onCommit: (entry: SettingsRegistryEntry, value: unknown) => Promise<void>;
    onClear: (entry: SettingsRegistryEntry) => Promise<void>;
    onReload: () => Promise<void>;
}) {
    const t = useT("editor");
    const resolveText = useSettingsTextResolver();
    const services = useExtensionShellServices();
    const CustomPanel = useExtensionSettingsPanel(group.id);
    const settingsEntry =
        group.entries.find((entry) => parseExtensionSettingsKey(entry.key)?.kind === "settings") ??
        null;

    if (!CustomPanel || !settingsEntry) {
        return (
            <GroupDetailPanel
                group={group}
                target={target}
                snapshot={snapshot}
                drafts={drafts}
                staleKeys={staleKeys}
                savingKeys={savingKeys}
                detailMode={detailMode}
                onDetailModeChange={onDetailModeChange}
                onDraftChange={onDraftChange}
                onDiscardDraft={onDiscardDraft}
                onCommit={onCommit}
                onClear={onClear}
                onReload={onReload}
            />
        );
    }

    const enabledValue = group.enabledEntry
        ? getCurrentLayerValue(
            group.enabledEntry,
            target,
            snapshot,
            drafts[group.enabledEntry.key],
        ) !== false
        : true;
    const meta = snapshot?.meta.entries[settingsEntry.key];
    const currentLayerValue = getCurrentLayerValue(
        settingsEntry,
        target,
        snapshot,
        drafts[settingsEntry.key],
    );
    const currentSettings = isRecord(currentLayerValue) ? currentLayerValue : {};
    const writable = isWritable(
        settingsEntry,
        target,
        snapshot?.capabilities,
        meta,
    );
    const clearable = !!meta?.setInTargets?.[target] && writable;
    const saving = savingKeys.includes(settingsEntry.key);
    const effect = meta?.effect ?? settingsEntry.effect;
    const props: SettingsPanelProps = {
        extensionId: group.id,
        settings: currentSettings,
        onChange: (next) => {
            if (!writable) return;
            void onCommit(settingsEntry, next);
        },
        t: makeExtensionTranslator(services, group.id),
    };

    return (
        <div className="min-h-full">
            <div className="border-b border-border pb-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                        <span className="mt-0.5 text-xl leading-none">{group.icon}</span>
                        <div className="min-w-0">
                            <div className="text-base text-foreground">
                                {resolveText(group.title, group.id)}
                            </div>
                            <div className="mt-1 text-[11px] font-mono text-muted-foreground">
                                {group.id}
                            </div>
                        </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <Badge>
                            {enabledValue
                                ? t("settings.boolean.enabled")
                                : t("settings.boolean.stopped")}
                        </Badge>
                        <MiniBadge>{targetLabel(target, resolveText)}</MiniBadge>
                        {effect ? (
                            <MiniBadge>{resolveText(EFFECT_LABELS[effect], effect)}</MiniBadge>
                        ) : null}
                        {!writable && meta?.readOnlyReason ? (
                            <MiniBadge>
                                {resolveText(meta.readOnlyReason, meta.readOnlyReason)}
                            </MiniBadge>
                        ) : null}
                        {clearable ? (
                            <button
                                type="button"
                                onClick={() => void onClear(settingsEntry)}
                                className="rounded-md border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                                {t("settings.clearTarget", {
                                    target: targetLabel(target, resolveText),
                                })}
                            </button>
                        ) : null}
                        {saving ? (
                            <Loader2
                                size={14}
                                className="shrink-0 animate-spin text-muted-foreground"
                            />
                        ) : null}
                    </div>
                </div>
                {group.description ? (
                    <div className="mt-2 pl-8 text-sm leading-6 text-muted-foreground">
                        {resolveText(group.description)}
                    </div>
                ) : null}
            </div>
            <div className="pt-3">
                {!writable ? (
                    <div className="mb-3 rounded-md border border-border/80 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                        {meta?.readOnlyReason
                            ? resolveText(meta.readOnlyReason, meta.readOnlyReason)
                            : t("settings.readOnly")}
                    </div>
                ) : null}
                <CustomPanel {...props} />
            </div>
        </div>
    );
}
